<template>
  <div class="container-responsive" style="padding-top: 1rem; padding-bottom: 1rem;">
    <!-- Notification Dialog -->
    <NotificationDialog
      v-model:visible="showNotification"
      :type="notificationType"
      :title="notificationTitle"
      :message="notificationMessage"
      @close="showNotification = false"
    />

    <v-card class="elevation-3" style="border-radius: 17px;">
      <v-card-title class="text-center" style="background-color: #002060; color: white; border-radius: 17px 17px 0 0;">
        <v-icon left>mdi-bug</v-icon>
        User Debug Tool
      </v-card-title>

      <v-card-text class="pa-6">
        <v-form ref="form" v-model="valid" lazy-validation>
          <v-row>
            <v-col cols="12">
              <h3 class="section-title mb-4">Check User Status</h3>
            </v-col>
            
            <v-col cols="12" md="8">
              <v-text-field
                v-model="emailToCheck"
                label="Email Address to Check"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-email"
                :rules="[
                  v => !!v || 'Email is required',
                  v => /.+@.+\..+/.test(v) || 'Must be a valid email'
                ]"
                placeholder="user@example.com"
              />
            </v-col>
            
            <v-col cols="12" md="4">
              <v-btn
                :disabled="!valid || loading"
                color="#002060"
                dark
                large
                rounded
                @click="checkUser"
                :loading="loading"
                block
              >
                <v-icon left>mdi-magnify</v-icon>
                Check User
              </v-btn>
            </v-col>
          </v-row>

          <!-- Results Section -->
          <v-row v-if="userResult" class="mt-4">
            <v-col cols="12">
              <h3 class="section-title mb-4">Results</h3>
              
              <v-card variant="outlined" class="pa-4">
                <v-card-title class="text-h6 mb-3">
                  <v-icon left :color="userResult.exists ? 'success' : 'error'">
                    {{ userResult.exists ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                  </v-icon>
                  User Status: {{ userResult.exists ? 'Found' : 'Not Found' }}
                </v-card-title>
                
                <div v-if="userResult.exists">
                  <v-list>
                    <v-list-item>
                      <v-list-item-title>Email:</v-list-item-title>
                      <v-list-item-subtitle>{{ userResult.data.email }}</v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-title>Name:</v-list-item-title>
                      <v-list-item-subtitle>{{ userResult.data.firstName }} {{ userResult.data.surname }}</v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-title>Role:</v-list-item-title>
                      <v-list-item-subtitle>{{ userResult.data.role }}</v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-title>Status:</v-list-item-title>
                      <v-list-item-subtitle>{{ userResult.data.status }}</v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-title>Employee ID:</v-list-item-title>
                      <v-list-item-subtitle>{{ userResult.data.employeeId }}</v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-title>Date Added:</v-list-item-title>
                      <v-list-item-subtitle>{{ userResult.data.dateAdded }}</v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-title>Firestore ID:</v-list-item-title>
                      <v-list-item-subtitle>{{ userResult.data.id }}</v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item v-if="userResult.data.uid">
                      <v-list-item-title>Firebase Auth UID:</v-list-item-title>
                      <v-list-item-subtitle>{{ userResult.data.uid }}</v-list-item-subtitle>
                    </v-list-item>
                  </v-list>
                </div>
                
                <div v-else>
                  <v-alert type="warning" variant="tonal" class="mb-3">
                    <strong>User not found in Firestore users collection</strong><br>
                    This email might be:
                    <ul class="mt-2">
                      <li>An orphaned Firebase Auth user (exists in Auth but not in Firestore)</li>
                      <li>A user that was never properly created</li>
                      <li>A user that was deleted from Firestore but not from Firebase Auth</li>
                    </ul>
                  </v-alert>
                  
                  <v-btn
                    color="warning"
                    variant="outlined"
                    @click="showFirebaseConsole"
                    class="mr-2"
                  >
                    <v-icon left>mdi-open-in-new</v-icon>
                    Check Firebase Console
                  </v-btn>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { getUserFromFirestore, debugUser } from '../utils/userCleanup';
import NotificationDialog from '../components/NotificationDialog.vue';

export default {
  name: 'UserDebug',
  components: {
    NotificationDialog
  },
  data() {
    return {
      valid: false,
      loading: false,
      showNotification: false,
      notificationType: 'success',
      notificationTitle: '',
      notificationMessage: '',
      emailToCheck: '',
      userResult: null
    };
  },
  methods: {
    async checkUser() {
      if (!this.$refs.form.validate()) return;
      
      this.loading = true;
      this.userResult = null;
      
      try {
        console.log(`Checking user: ${this.emailToCheck}`);
        
        // Get user from Firestore
        const userData = await getUserFromFirestore(this.emailToCheck);
        
        this.userResult = {
          exists: !!userData,
          data: userData
        };
        
        // Also run debug function for console output
        await debugUser(this.emailToCheck);
        
        if (userData) {
          this.notificationType = 'success';
          this.notificationTitle = 'User Found!';
          this.notificationMessage = `User ${userData.firstName} ${userData.surname} found in the system.`;
        } else {
          this.notificationType = 'warning';
          this.notificationTitle = 'User Not Found';
          this.notificationMessage = 'This email is not found in the Firestore users collection.';
        }
        
        this.showNotification = true;
        
      } catch (error) {
        console.error('Error checking user:', error);
        this.notificationType = 'error';
        this.notificationTitle = 'Error!';
        this.notificationMessage = 'Failed to check user. Please try again.';
        this.showNotification = true;
      } finally {
        this.loading = false;
      }
    },
    
    showFirebaseConsole() {
      // Open Firebase Console in new tab
      window.open('https://console.firebase.google.com/project/dmm-law-user-portal/authentication/users', '_blank');
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap');

* {
  font-family: 'Montserrat', sans-serif;
}

.section-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #002060;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 0.5rem;
}

:deep(.v-field--variant-solo) {
  background-color: #f8f9fa !important;
}

:deep(.v-field--variant-solo .v-field__overlay) {
  background-color: #f8f9fa !important;
}
</style>









